import extensionsStorage from './extensions-storage';
import { REGISTERED_EXTENSIONS_TYPE, parseRegisteredExtensions } from '../../../assets/registered-extensions';

const coreExtensionName: string = 'main.core';
const registeredNames: Set<string> = new Set();

const consumedScripts: WeakSet<Element> = new WeakSet();
let documentParsed: boolean = false;

// Derived from the same set the server is told about: a separate boolean would
// be a second source of truth for "the core is registered" and could drift from
// the reported list.
export function isCoreRegistered(): boolean
{
	return registeredNames.has(coreExtensionName);
}

// Names the client already runs, reported to the server so it can skip their
// branches. Only actual registrations count: a name queued by loadExtension is
// still "in flight", and skipping it for another batch would let that batch run
// before the phrases and settings of the branch have been applied (the ordering
// "data before assets" holds inside a batch, not between batches).
export function getRegisteredNames(): Array<string>
{
	return [...registeredNames];
}

// Records a registration without touching the extensions storage. After a
// minimal->full upgrade the registration itself goes through the freshly loaded
// BX.Runtime (a different module instance with its own storage), while the state
// gating the request parameters lives in this bundle and must stay in sync.
export function markRegistered(name: string): void
{
	if (typeof name === 'string' && name !== '')
	{
		registeredNames.add(name);
	}
}

// Test-only: resets the module-level registration state between unit tests.
export function resetRegisteredForTests(): void
{
	registeredNames.clear();
	documentParsed = false;
}

export default function registerExtension(options: any): void
{
	markRegistered(options?.name);

	if (!extensionsStorage.has(options.name))
	{
		const namespace = options?.namespace ?? 'window';
		extensionsStorage.set(options.name, Promise.resolve(namespace));
	}
}

// Registers the extensions a response or a fragment has just loaded against the currently
// active BX.Runtime. On a minimal->full upgrade the loaded core.js replaces BX.Runtime with
// a new instance owning a fresh extensions storage; registering there (not into this bundle's
// captured storage) is what keeps a later loadExtension from going back to the network.
// The state gating the request parameters lives in this bundle, so it is synced separately.
export function registerInActiveRuntime(namespaces: Readonly<Record<string, string>>): void
{
	const runtime: any = (window as any).BX?.Runtime;

	Object.entries(namespaces).forEach(([name, namespace]: [string, string]) => {
		if (typeof runtime?.registerExtension === 'function')
		{
			runtime.registerExtension({ name, namespace });
		}
		else
		{
			registerExtension({ name, namespace });
		}

		markRegistered(name);
	});
}

// The server marks the extensions of a page as loaded with inert blocks instead of
// an inline call per extension. The blocks of the page markup are read lazily, right
// before the registry is consulted, and for the last time once the document is parsed.
// A block that appears later is not trusted: inserted as plain markup, its scripts may
// never have run. Fragments are registered by their own pipelines instead, after their
// scripts: extractAssets hands a block back as `registrations` for the consumer to apply,
// HttpClient applies it after the asset loads.
export function registerFromDocument(): void
{
	if (documentParsed)
	{
		return;
	}

	documentParsed = document.readyState !== 'loading';

	document.querySelectorAll(`script[type="${REGISTERED_EXTENSIONS_TYPE}"]`).forEach((script: Element) => {
		if (!consumedScripts.has(script))
		{
			consumedScripts.add(script);
			// every runtime instance reads the page into its own storage
			Object.entries(parseRegisteredExtensions(script.textContent ?? '')).forEach(([name, namespace]) => {
				registerExtension({ name, namespace });
			});
		}
	});
}

if (typeof document !== 'undefined' && document.readyState === 'loading')
{
	document.addEventListener('DOMContentLoaded', () => registerFromDocument(), { once: true });
}
